import { Box, Typography, Alert } from '@mui/material';
import { CallDto } from '@act/shared/data-transfer-objects';
import { IconLibrary, FormBuilder } from '@act/storybook';
import { formatPhoneNumber } from '@act/shared/utilities';
import { utilityAppSelectors } from 'redux/utils/selectors/utility-selectors';
import { displayName } from 'utilities/formatters/display-name';
import { useState, useEffect, useMemo, useRef } from 'react';
import { callsAppSelectors } from 'redux/slices/callsSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { callsRequests } from 'redux/requests/calls.request';
import { useForm, Controller } from 'react-hook-form';
import { useToast } from 'react-components/app/ReusableToast';
import {
  SelectOption,
  SearchBar,
} from 'react-components/components/search-bar';
import { userAppSelectors } from 'redux/slices/usersSlice';
import { getTeam } from 'redux/slices/usersSlice';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { membersRequests } from 'redux/requests/members.request';
import { Button, IconButton } from '@act/storybook';
import useConfirmationAlertDialog from '@act/react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import { AssignPhoneNumberFormComponent } from 'react-components/form-dialog/AssignPhoneNumberForm';
import { TrackingProvider } from 'react-components/tracking';

export interface ActiveCallMenuProps {
  call: CallDto;
  showConnectionBorders?: boolean;
}

interface AddCallerForm {
  guideId: string | null;
  memberId?: string | null;
}

const callConnectionStatusesToIgnore = [
  'failed',
  'busy',
  'no-answer',
  'canceled',
];

export const ActiveCallMenu = ({
  call: initialCall,
  showConnectionBorders = false,
}: ActiveCallMenuProps) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const openConfirmationDialog = useConfirmationAlertDialog();
  const [isAddingConnection, setIsAddingConnection] = useState(false);
  const [isAssigningNumber, setIsAssigningNumber] = useState(false);
  const [guideOptions, setGuideOptions] = useState<SelectOption[]>([]);
  const [memberOptions, setMemberOptions] = useState<SelectOption[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  // Get the call from Redux store to receive real-time updates
  const call =
    callsAppSelectors.useSelectCallById(initialCall?.id) || initialCall;

  // Get current user to filter out their connection
  const actingUser = actingUserAppSelectors.useSelectActingUser();

  // Filter out the current user's connection from the list
  // Keep the member's connection so guides can control it
  // Filter out unknown connections (null userId)
  const allConnections = call?.connections || [];
  const connections = useMemo(() => {
    if (!actingUser?.id) return allConnections;
    return allConnections.filter(
      connection => connection.userId !== actingUser.id
    );
  }, [allConnections, actingUser?.id]);

  // Find the guide's own active connection to check if they've answered
  // If the guide left and rejoined, find their active connection, not the completed one
  const guideConnection = useMemo(() => {
    if (!actingUser?.id) return null;
    return allConnections.find(
      connection =>
        connection.userId === actingUser.id &&
        !connection.completedAt &&
        connection.status !== 'failed'
    );
  }, [allConnections, actingUser?.id]);

  // Check if guide has answered their phone
  const guideHasAnswered = !!guideConnection?.connectedAt;

  // Check if member is known (has subjectId)
  const isMemberKnown = !!call?.subjectId;

  // Determine the main connection.
  // either the originator if incoming, or the receiver if outgoing.
  const mainConnection = call.incoming ? call.originator : call.receiver;

  // Form for adding caller
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState,
    formState: { isValid },
  } = useForm<AddCallerForm>({
    defaultValues: {
      guideId: null,
      memberId: null,
    },
  });

  const guideId = watch('guideId');
  const memberId = watch('memberId');

  // Load guides
  const viewableGuides = userAppSelectors.useSelectCurrentGuides();

  // Get selected member from utility selectors (hook must be called at top level)
  const selectedMemberFromStore = utilityAppSelectors.selectUserOrMemberById(
    memberId || ''
  );

  // Fuzzy search for members
  const [typeaheadMembers, isLoadingTypeaheadMembers, getTypeaheadMembers] =
    useApiCall(membersRequests.getPaginatedMembersForTable);

  // Get the known member if caller is known
  const knownMember = utilityAppSelectors.selectUserOrMemberById(
    call?.subjectId || ''
  );

  useEffect(() => {
    if (viewableGuides) {
      let options = viewableGuides.map(guide => ({
        label: guide.name,
        value: guide.id,
      }));

      // Determine which member to use: known member from call.subjectId or selected member from form
      let memberToCheck = null;
      if (isMemberKnown && knownMember) {
        // Use the known member when caller is known
        memberToCheck = knownMember;
      } else if (memberId) {
        // Use the selected member from form for unknown callers
        // Try to get the member from search results first, then fall back to store
        const selectedMemberFromResults = typeaheadMembers?.users?.find(
          m => m.id === memberId
        );
        memberToCheck = selectedMemberFromResults || selectedMemberFromStore;
      }

      // If we have a member to check, feature their assigned guide as the first option
      if (memberToCheck?.assignedGuideId) {
        const assignedGuideId = memberToCheck?.assignedGuideId;
        const assignedGuideIndex = options.findIndex(
          opt => opt.value === assignedGuideId
        );

        if (assignedGuideIndex > -1) {
          // Find the full guide object to get firstName and lastName
          const assignedGuideObject = viewableGuides.find(
            g => g.id === assignedGuideId
          );

          // Remove the assigned guide from its current position
          const assignedGuide = options[assignedGuideIndex];

          // Update the label to show firstName lastName (assigned guide)
          const assignedGuideLabel = assignedGuideObject
            ? `${assignedGuideObject.firstName} ${assignedGuideObject.lastName} (assigned guide)`
            : assignedGuide.label;

          options = [
            { ...assignedGuide, label: assignedGuideLabel },
            ...options.filter(opt => opt.value !== assignedGuideId),
          ];
        }
      }

      setGuideOptions(options);
    }
  }, [
    viewableGuides,
    memberId,
    typeaheadMembers,
    selectedMemberFromStore,
    isMemberKnown,
    knownMember,
  ]);

  // Track selected member option separately to preserve it across searches
  const [selectedMemberOption, setSelectedMemberOption] =
    useState<SelectOption | null>(null);

  // Update member options when typeahead results change
  // Always include the selected member option if one is selected
  useEffect(() => {
    if (typeaheadMembers?.users) {
      // Create a map to deduplicate by member ID
      const optionsMap = new Map<string, SelectOption>();

      // Get the call's phone number for comparison
      const callPhoneNumber = mainConnection?.phoneNumber;

      // Add all members from search results, deduplicating by ID
      typeaheadMembers.users.forEach(member => {
        if (!optionsMap.has(member.id)) {
          const baseName =
            member.name || `${member.firstName} ${member.lastName}`;

          // Check if this member has the phone number assigned
          const hasPhoneNumber =
            callPhoneNumber &&
            member.phoneNumbers?.some(
              phone => phone.number === callPhoneNumber
            );

          const label = hasPhoneNumber
            ? `${baseName} (assigned to this member)`
            : baseName;

          optionsMap.set(member.id, {
            value: member.id,
            label,
          });
        }
      });

      // If a member is selected, ensure it's in the options
      if (memberId) {
        const selectedInResults = typeaheadMembers.users.find(
          m => m.id === memberId
        );
        if (selectedInResults) {
          // Update selectedMemberOption with the fresh data from search results
          const baseName =
            selectedInResults.name ||
            `${selectedInResults.firstName} ${selectedInResults.lastName}`;

          // Check if this member has the phone number assigned
          const hasPhoneNumber =
            callPhoneNumber &&
            selectedInResults.phoneNumbers?.some(
              phone => phone.number === callPhoneNumber
            );

          const label = hasPhoneNumber
            ? `${baseName} (assigned to this member)`
            : baseName;

          const freshOption = {
            value: selectedInResults.id,
            label,
          };
          setSelectedMemberOption(freshOption);
          // Ensure it's in the map (in case of duplicates in API response)
          optionsMap.set(memberId, freshOption);
        } else if (selectedMemberOption) {
          // Selected member not in current results, add stored option to the list
          if (!optionsMap.has(memberId)) {
            optionsMap.set(memberId, selectedMemberOption);
          }
        }
      }

      // Convert map to array, ensuring no duplicates
      const options = Array.from(optionsMap.values());
      setMemberOptions(options);
      setIsLoadingMembers(false);
    } else {
      // Keep selected option even when no search results
      if (memberId && selectedMemberOption) {
        setMemberOptions([selectedMemberOption]);
      } else {
        setMemberOptions([]);
      }
    }
  }, [
    typeaheadMembers,
    memberId,
    selectedMemberOption,
    mainConnection?.phoneNumber,
  ]);

  // Track if we're in search mode vs default mode
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState<string>('');
  const lastLoadedGuideId = useRef<string | null>(null);
  const lastLoadState = useRef<string>('');

  // Load default members when form opens (shows members from any guide)
  // This effect runs when form opens, when search is cleared, or when guideId changes
  useEffect(() => {
    // Create a unique key for the current load state
    const currentLoadState = `${isAddingConnection}-${isSearchMode}-${lastSearchTerm}-${guideId}`;
    const guideIdChanged = lastLoadedGuideId.current !== guideId;
    const stateChanged = lastLoadState.current !== currentLoadState;

    const shouldLoad =
      isAddingConnection &&
      !isSearchMode &&
      !lastSearchTerm &&
      !isLoadingMembers &&
      (stateChanged || guideIdChanged);

    if (shouldLoad) {
      lastLoadedGuideId.current = guideId;
      lastLoadState.current = currentLoadState;
      setIsLoadingMembers(true);
      getTypeaheadMembers({
        params: {
          'page-start': 0,
          'page-size': 50,
          includeCount: false,
          includeDetails: true, // Include phone numbers for display
          ...(guideId ? { guideIds: [guideId] } : {}),
        },
      });
    }
  }, [
    isAddingConnection,
    isSearchMode,
    lastSearchTerm,
    isLoadingMembers,
    guideId,
    // getTypeaheadMembers is intentionally excluded to prevent infinite loops
    // It's stable from useApiCall hook and only used inside the effect
  ]);

  // Reset the tracked state when form closes
  useEffect(() => {
    if (!isAddingConnection) {
      lastLoadedGuideId.current = null;
      lastLoadState.current = '';
    }
  }, [isAddingConnection]);

  // Function to search members (SearchBar already debounces this)
  const setTypeaheadSearchTerm = (searchTerm: string) => {
    setLastSearchTerm(searchTerm);
    if (searchTerm && searchTerm.length >= 2) {
      setIsSearchMode(true);
      setIsLoadingMembers(true);

      // Build params object with required fields
      const params = {
        searchTerm,
        'page-start': 0,
        'page-size': 20,
        includeCount: false,
        includeDetails: true, // Include phone numbers for display
        // If a guideId is explicitly selected, use it to filter results
        // Otherwise, fall back to no guideId filtering
        // Backend expects guideIds as a string array (will be converted to comma-delimited string)
        ...(guideId ? { guideIds: [guideId] } : {}),
      };

      getTypeaheadMembers({ params });
    } else {
      // Reset to default mode when search is cleared
      setIsSearchMode(false);
      setMemberOptions([]);
      setIsLoadingMembers(false);
      // Reset the tracked state so defaults can be loaded again
      lastLoadedGuideId.current = null;
      lastLoadState.current = '';
    }
  };

  useEffect(() => {
    if (!viewableGuides) {
      void dispatch(getTeam({ params: { allViewable: true } }));
    }
  }, [viewableGuides, dispatch]);

  // API call to add guide
  const [addedGuide, isAddingGuideToCall, addGuideToCall] = useApiCall(
    callsRequests.addGuideToCall
  );

  // API call to merge connections
  const [mergedCall, isMergingCalls, mergeCallConnections] = useApiCall(
    callsRequests.mergeCallConnections
  );

  // API call to hangup guide's own connection
  const [_hangupResult, isHangingUpGuide, hangupGuideConnection] = useApiCall(
    callsRequests.hangupConnection
  );

  // Track the last processed guide addition to prevent duplicate processing
  const lastProcessedGuideRef = useRef<typeof addedGuide>(null);

  useEffect(() => {
    // Only process if we have a new result (different from last processed)
    if (
      addedGuide &&
      !isAddingGuideToCall &&
      addedGuide !== lastProcessedGuideRef.current
    ) {
      toast('Guide added successfully', 'success');
      reset();
      setIsAddingConnection(false);
      lastProcessedGuideRef.current = addedGuide;
    }
  }, [addedGuide, isAddingGuideToCall, toast, reset]);

  useEffect(() => {
    if (mergedCall && !isMergingCalls) {
      toast('Calls merged successfully', 'success');
    }
  }, [mergedCall, isMergingCalls, toast]);

  const onSubmitAddCaller = (data: AddCallerForm) => {
    if (!data.guideId) {
      toast('Please select a guide', 'error');
      return;
    }

    addGuideToCall({
      params: { callId: call.id },
      body: {
        guideId: data.guideId,
        isColdTransfer: false,
        ...(data.memberId && { memberId: data.memberId }),
      },
    });
  };

  const getConnectionStatus = (connection: CallDto['connections'][0]) => {
    if (connection.onHold) return 'On Hold';

    // Check for failed connection states (including completed but never connected)
    const status = connection.status?.toLowerCase();
    if (
      status === 'failed' ||
      status === 'busy' ||
      status === 'no-answer' ||
      status === 'canceled' ||
      // Connection completed without ever connecting (not answered)
      (connection.completedAt && !connection.connectedAt)
    ) {
      return 'Unable to connect';
    }

    if (connection.completedAt) return 'Completed';
    if (connection.connectedAt) return 'Connected';

    return connection.status || 'Unknown';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'connected':
        return 'success';
      case 'on hold':
        return 'warning';
      case 'completed':
        return 'default';
      case 'failed':
      case 'busy':
      case 'no-answer':
      case 'canceled':
      case 'unable to connect':
        return 'error';
      default:
        return 'info';
    }
  };

  // Form configuration for adding caller
  const addCallerFormConfig: FormConfig<AddCallerForm> = useMemo(() => {
    const config: FormConfig<AddCallerForm> = [];

    // Add an empty option at the beginning to allow clearing the selection
    const guideOptionsWithClear = [
      { label: 'All guides', value: null },
      ...guideOptions,
    ];

    config.push({
      type: 'input',
      name: 'guideId',
      label: 'Guide name',
      input: 'select',
      props: {
        options: guideOptionsWithClear,
        required: false,
      },
    });

    return config;
  }, [guideOptions]);

  const handleMergeCall = () => {
    mergeCallConnections({
      body: { callId: call.id },
    });
  };

  const handleLeaveCall = async () => {
    if (!guideConnection) return;

    // Count active connections (not completed)
    const activeConnectionsCount = allConnections.filter(
      conn =>
        !conn.completedAt &&
        !callConnectionStatusesToIgnore.includes(conn.status)
    ).length;

    // Determine warning message based on connection count
    const willCallContinue = activeConnectionsCount >= 3;
    const message = willCallContinue
      ? 'You will leave the call and the existing participants will continue their call.'
      : 'Are you sure you want to leave the call? This will end the call for all participants.';

    const shouldLeave = await openConfirmationDialog({
      title: 'Leave call confirmation',
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      icon: <IconLibrary iconName="Warning" size={42} color="white" />,
      message,
      confirmationText: 'Leave call',
      confirmButtonColor: 'error',
    });

    if (shouldLeave) {
      hangupGuideConnection({
        body: {
          connectionId: guideConnection.id,
        },
      });
    }
  };

  // Check if the main connection (receiver) is on hold
  const isMainConnectionOnHold = mainConnection?.onHold ?? false;

  // Count active call connections (not completed and not canceled/no-answer)
  // This includes connected, on hold, and ringing connections
  const activeConnectionsCount = useMemo(() => {
    return allConnections.filter(
      connection =>
        !connection.completedAt &&
        !callConnectionStatusesToIgnore.includes(connection.status)
    ).length;
  }, [allConnections]);
  // Hide "Add caller" button if there are 3 or more active connections
  const shouldShowAddCallerButton = activeConnectionsCount < 3;

  return (
    <TrackingProvider contextName="active-call-menu">
      <Box>
        {/* Message when guide hasn't answered their phone yet */}
        {!guideHasAnswered ? (
          <Box
            sx={{
              padding: '24px',
              backgroundColor: 'white',
              borderRadius: '12px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="bodyMedium"
              color="text.secondary"
              sx={{
                fontStyle: 'italic',
              }}
            >
              Answer the call on your phone to display full call controls
            </Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                justifyContent: 'center',
                marginBottom: '24px',
              }}
            >
              {isMainConnectionOnHold && (
                <IconButton
                  color="primary"
                  size="large"
                  onClick={handleMergeCall}
                  label="Merge"
                  disabled={isMergingCalls}
                  trackingName="merge-call-button"
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  <IconLibrary
                    iconName="ArrowsLeftRight"
                    weight="regular"
                    size={18}
                  />
                </IconButton>
              )}
              {!isMemberKnown && (
                <IconButton
                  color="primary"
                  size="large"
                  active={isAssigningNumber}
                  onClick={() => {
                    setIsAssigningNumber(!isAssigningNumber);
                    // Close the other form if opening this one
                    if (!isAssigningNumber) {
                      setIsAddingConnection(false);
                    }
                  }}
                  label="Assign number"
                  trackingName="assign-phone-number-button"
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  <IconLibrary
                    iconName="UserCirclePlus"
                    weight="regular"
                    size={22}
                  />
                </IconButton>
              )}
              {shouldShowAddCallerButton && (
                <IconButton
                  color="primary"
                  size="large"
                  active={isAddingConnection}
                  onClick={() => {
                    setIsAddingConnection(!isAddingConnection);
                    // Close the other form if opening this one
                    if (!isAddingConnection) {
                      setIsAssigningNumber(false);
                    }
                  }}
                  label="Add caller"
                  trackingName="add-caller-button"
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  <IconLibrary
                    iconName="PhonePlus"
                    weight="regular"
                    size={22}
                  />
                </IconButton>
              )}
            </Box>

            {/* Connections displayed horizontally */}
            {connections.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '24px',
                  flexWrap: 'wrap',
                }}
              >
                {connections.map(connection => (
                  <ConnectionCard
                    key={connection.id}
                    connection={connection}
                    activeConnectionsCount={activeConnectionsCount}
                    getConnectionStatus={getConnectionStatus}
                    getStatusColor={getStatusColor}
                    showBorder={showConnectionBorders}
                  />
                ))}
              </Box>
            )}

            {/* Assign Phone Number Form */}
            {isAssigningNumber && mainConnection?.phoneNumber && (
              <TrackingProvider contextName="assign-phone-number-form">
                <Box
                  sx={{
                    padding: '16px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    marginBottom: '24px',
                  }}
                >
                  <AssignPhoneNumberFormComponent
                    phoneNumber={mainConnection.phoneNumber}
                    callId={call.id}
                    onSuccess={() => {
                      setIsAssigningNumber(false);
                      toast('Phone number assigned successfully!', 'success');
                    }}
                    onCancel={() => setIsAssigningNumber(false)}
                    showActions={true}
                    showAlert={true}
                    showTitle={true}
                  />
                </Box>
              </TrackingProvider>
            )}

            {/* Add Connection Form */}
            {isAddingConnection && (
              <TrackingProvider contextName="add-connection-form">
                <Box
                  sx={{
                    padding: '16px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    marginBottom: '24px',
                  }}
                >
                  <Typography
                    variant="h6"
                    color="text.primary"
                    fontWeight={600}
                    sx={{ marginBottom: '16px' }}
                  >
                    Select guide to add to the call
                  </Typography>

                  {isValid && (
                    <Alert
                      severity="info"
                      variant="standard"
                      sx={{ mb: '16px' }}
                    >
                      Remember to notify the member that they will be on a brief
                      hold and the line may go silent.
                    </Alert>
                  )}

                  {/* Member selection for unknown callers */}
                  {!isMemberKnown && (
                    <Controller
                      control={control}
                      name="memberId"
                      render={({ field: { onChange, value } }) => {
                        // Find the selected option - prefer from current options, fallback to stored option
                        const selectedOption = value
                          ? memberOptions.find(opt => opt.value === value) ||
                            selectedMemberOption
                          : null;

                        return (
                          <Box sx={{ marginBottom: '16px' }}>
                            <SearchBar
                              label="Member's name (optional)"
                              options={memberOptions}
                              isLoading={
                                isLoadingMembers || isLoadingTypeaheadMembers
                              }
                              setTypeaheadSearchTerm={setTypeaheadSearchTerm}
                              setSearchTerm={(
                                searchTerm: string | SelectOption
                              ) => {
                                if (
                                  typeof searchTerm === 'object' &&
                                  searchTerm?.value
                                ) {
                                  // Option selected from dropdown
                                  setSelectedMemberOption(searchTerm);
                                  onChange(searchTerm.value);
                                } else if (
                                  typeof searchTerm === 'string' &&
                                  searchTerm.trim() === ''
                                ) {
                                  // Input cleared
                                  setSelectedMemberOption(null);
                                  onChange(null);
                                }
                                // For free text input, don't change the value
                              }}
                              value={selectedOption?.label || ''}
                            />
                          </Box>
                        );
                      }}
                    />
                  )}

                  <FormBuilder
                    config={addCallerFormConfig}
                    control={control}
                    formState={formState}
                  />
                  {isAddingConnection && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '16px',
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmitAddCaller)}
                        disabled={isAddingGuideToCall || !guideId || !isValid}
                        trackingName="submit-add-caller-button"
                      >
                        Add caller
                      </Button>
                    </Box>
                  )}
                </Box>
              </TrackingProvider>
            )}

            {/* Leave Call Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'right',
                marginTop: '24px',
              }}
            >
              <Button
                variant="outlined"
                onClick={handleLeaveCall}
                disabled={isHangingUpGuide || !guideConnection}
                trackingName="leave-call-button"
                startIcon={
                  <IconLibrary
                    iconName="SignOut"
                    size={18}
                    weight="regular"
                    color="error"
                  />
                }
                sx={{
                  borderColor: 'error.main',
                  color: 'error.main',
                }}
              >
                Leave call
              </Button>
            </Box>
          </>
        )}
      </Box>
    </TrackingProvider>
  );
};

interface ConnectionCardProps {
  connection: CallDto['connections'][0];
  activeConnectionsCount: number;
  getConnectionStatus: (connection: CallDto['connections'][0]) => string;
  getStatusColor: (
    status: string
  ) => 'success' | 'warning' | 'default' | 'error' | 'info';
  showBorder?: boolean;
}

const ConnectionCard = ({
  connection,
  activeConnectionsCount,
  showBorder = false,
}: ConnectionCardProps) => {
  const openConfirmationDialog = useConfirmationAlertDialog();
  const user = utilityAppSelectors.selectUserOrMemberById(connection.userId);
  const resourceTitle =
    typeof connection.config?.title === 'string'
      ? connection.config.title
      : undefined;
  const connectionDisplayName = user
    ? displayName(user)
    : resourceTitle || 'Unknown';

  const isOnHold = connection.onHold;
  const isConnected = !!connection.connectedAt && !connection.completedAt;
  const isRinging =
    !connection.connectedAt &&
    !connection.completedAt &&
    !callConnectionStatusesToIgnore.includes(connection.status);

  // Create a tracking context name based on connection type
  const trackingContextName = user
    ? 'connection-card'
    : 'unknown-connection-card';

  // Check if connection failed
  const status = connection.status?.toLowerCase();
  const isFailed =
    status === 'failed' ||
    status === 'busy' ||
    status === 'no-answer' ||
    status === 'canceled' ||
    // Connection completed without ever connecting (not answered)
    (!!connection.completedAt && !connection.connectedAt);

  const isCanceled = callConnectionStatusesToIgnore.includes(connection.status);
  const isCompleted = !!connection.completedAt;

  // API call to toggle hold
  const [_toggledHold, isTogglingHold, toggleConnectionHold] = useApiCall(
    callsRequests.toggleConnectionHold
  );

  // API call to hangup connection
  const [_hangupConnection, isHangingUp, hangupConnection] = useApiCall(
    callsRequests.hangupConnection
  );

  const handleHold = () => {
    toggleConnectionHold({
      body: {
        connectionId: connection.id,
        onHold: !isOnHold,
      },
    });
  };

  const handleHangup = async () => {
    // Determine warning message based on connection state and count
    let message: string;
    let title: string;
    let confirmationText: string;

    if (isRinging) {
      // For ringing connections, use cancel language
      title = 'Cancel call confirmation';
      message = `Are you sure you want to cancel the call to ${connectionDisplayName}?`;
      confirmationText = 'Cancel call';
    } else {
      // For connected calls, use hangup language
      title = 'Hang up call confirmation';
      const willCallContinue = activeConnectionsCount >= 3;
      message = willCallContinue
        ? `${connectionDisplayName} will leave the call and the existing participants will continue their call.`
        : `Are you sure you want to remove ${connectionDisplayName}?`;
      confirmationText = 'Hang up';
    }

    const shouldHangup = await openConfirmationDialog({
      title,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      icon: <IconLibrary iconName="Warning" size={42} color="white" />,
      message,
      confirmationText,
      confirmButtonColor: 'error',
    });
    if (shouldHangup) {
      hangupConnection({
        body: {
          connectionId: connection.id,
        },
      });
    }
  };

  let iconName = 'Phone';
  if (isFailed) {
    iconName = 'PhoneX';
  } else if (isCanceled || isCompleted) {
    iconName = 'PhoneEnded';
  }

  return (
    <TrackingProvider contextName={trackingContextName}>
      <Box
        sx={{
          position: 'relative',
          flex: showBorder ? '0 0 calc(50% - 42px)' : '1 1 300px',
          minWidth: '280px',
          padding: '8px',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          ...(showBorder && {
            padding: '16px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }),
          ...(isCompleted && {
            opacity: 0.5,
          }),
        }}
      >
        {/* Grey overlay for completed connections */}
        {isCompleted && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(128, 128, 128, 0.3)',
              borderRadius: showBorder ? '8px' : 0,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
        {/* Phone Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          }}
        >
          <IconLibrary
            iconName={iconName}
            size={20}
            weight="regular"
            color={isFailed ? 'white' : undefined}
          />
        </Box>

        {/* Name and Phone Number */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '4px',
            }}
          >
            <Typography
              variant="bodyMedium"
              fontWeight={600}
              color={isFailed ? 'error.main' : 'text.primary'}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {connectionDisplayName}
            </Typography>
          </Box>
          <Typography
            variant="bodySmall"
            color={isFailed ? 'error.main' : 'text.secondary'}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {formatPhoneNumber(connection.phoneNumber)}
          </Typography>
        </Box>

        {/* Unable to connect message on the right */}
        {isFailed && (
          <Typography
            variant="bodySmall"
            color="error.main"
            sx={{
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            Unable to connect
          </Typography>
        )}

        {/* Action Buttons - show for connected or ringing connections */}
        {(isConnected || isRinging) && (
          <>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Hangup Button */}
              <IconButton
                onClick={handleHangup}
                color="error"
                active={true}
                size="large"
                disabled={isHangingUp}
                aria-label={isRinging ? 'Cancel call' : 'Hang up'}
                trackingName={
                  isRinging
                    ? 'cancel-ringing-connection-button'
                    : 'hangup-connection-button'
                }
                sx={{
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#B71C1C', // Darker red on hover
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
              >
                <IconLibrary
                  iconName="PhoneEnded"
                  size={18}
                  weight="regular"
                  color="white"
                />
              </IconButton>
            </Box>
            {/* Only show hold button for connected calls */}
            {isConnected && !isOnHold ? (
              <Button
                onClick={handleHold}
                variant="outlined"
                disabled={isTogglingHold}
                startIcon={
                  <IconLibrary
                    iconName="PhonePause"
                    size={18}
                    weight="regular"
                    color="white"
                  />
                }
                aria-label="Hold"
                trackingName="hold-connection-button"
              >
                Hold
              </Button>
            ) : isConnected && isOnHold ? (
              <Button
                onClick={handleHold}
                variant="contained"
                disabled={isTogglingHold}
                startIcon={
                  <IconLibrary iconName="Pause" size={18} color="white" />
                }
                aria-label="Resume"
                trackingName="resume-connection-button"
              >
                Resume
              </Button>
            ) : null}
          </>
        )}
      </Box>
    </TrackingProvider>
  );
};
